/**
 * useAdvanceState — the advance dossier's ONE selector.
 *
 * `useContractSetupState` FOR A `loans` ROW: it reads what is cached, shapes it into an
 * `AdvanceSnapshot` and hands that to `computeAdvanceState`, so the rail, the narrator, the three
 * progress figures and every canvas read come out of ONE derivation. The impure half is all that
 * lives here — which caches are read, when the clock ticks, and how a lazily-arriving render finds
 * its way in; the rules are in `advanceState.ts`, which is React-free and tested.
 *
 * THE BOARD'S SUBJECTS ARE THE ADVANCE'S OWN READ, NOT A SECOND ONE. `useContractAgreements` at
 * `junctionScope: 'all'` already fetches every `loans` row of this contract with its rate history,
 * its purpose, its rate and its disbursement's amount (`SUBJECT_GENERAL_LOANS_SELECT`) — that is
 * what it mints the LOAN slot from. Issuing a second `loans` read here would be two answers to one
 * question, which is exactly the shape that made the insurance canvas grade one policy year while
 * describing another. So the page passes its model in and this hook reads off it.
 *
 * FOUR THINGS THE BOARD CANNOT ANSWER, AND EACH GETS A READ OF ITS OWN:
 *
 * - THE DISBURSEMENT'S DATE. The subjects embed asks the transaction for its `amount` alone, and
 *   the rail prints the Ghana day the money moved beside it.
 * - THE PARTIES. The Schedule prints the Borrower's name, address and phone and the Vehicle's model
 *   and registration; the board's contract read carries the driver's NAME and the vehicle's OWNER,
 *   because those are what it gates on. Five render-blocking keys are not derivable from it.
 * - THE FORWARD WEEKLY TOTAL. Every advance's repayment is ADDED to the contract instalment [D-B54],
 *   so the repayment area states what the driver will owe every Monday: the latest contract
 *   instalment plus every standing advance's current rate. `weekly_rate` is NOT read for it — see
 *   `advanceArithmetic.ts` for why that figure answers a different question.
 * - THE RECOVERY WATERMARK [D-B54]. Whether this advance predates the weekly-schedule rule is
 *   `loans.id < allocation_gate_watermarks.below_id` for `vehicle_recovery_gate`, which is a table
 *   nothing else on this page touches.
 *
 * THE WATERMARK FAILS CONSERVATIVE. A read that errors, or a row that is not there, leaves the
 * advance in the LEGACY arm — repayment read-only, no write offered. The opposite default writes a
 * schedule onto a pre-watermark advance, which admits it to the allocator at every repayment since
 * disbursement and re-splits history across 32 contracts; the cost of being wrong in this direction
 * is a door that has to be opened again, and in the other it is money that has already moved.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { ContractAgreementsModel } from '@/components/agreements/AgreementSlotList';
import { loanSlotsForLoan, type BoardSlot } from '@/components/agreements/boardSlots';
import type { AgreementRenderPayload } from '@/lib/agreements/api';
import { driverProfileOf } from '@/lib/driverProfile';
import { supabase } from '@/integrations/supabase/client';

import type { WeeklyInstalmentRow } from './advanceArithmetic';
import type { AdvanceExisting } from './advanceCommit';
import {
    advanceLegacyReason,
    computeAdvanceState,
    firstStartsAt,
    latestInstalment,
    resolveAdvanceLandingArea,
    type AdvanceInstalmentFact,
    type AdvanceLegacyReason,
    type AdvanceSnapshot,
    type AdvanceState,
} from './advanceState';
import type { AdvanceArea } from './advanceSteps';

/** How often the seal counter re-reads the clock while a finalize is genuinely outstanding. */
const SEAL_TICK_MS = 1_000;

/** The allocator gate [D-B54] reads this rule; `below_id` is the first id in the NEW arm. */
const RECOVERY_GATE_RULE = 'vehicle_recovery_gate';

export const advancePartiesKey = (contractId: number) => ['advance-parties', contractId] as const;
export const advanceForwardKey = (contractId: number) => ['advance-forward', contractId] as const;
export const advanceTransactionKey = (transactionId: number | null) =>
    ['advance-transaction', transactionId] as const;
export const RECOVERY_WATERMARK_KEY = ['allocation-gate-watermark', RECOVERY_GATE_RULE] as const;

// ─── The reads ───────────────────────────────────────────────────────────────

interface PartyFacts {
    driver: { fullName: string | null; phone: string | null; address: string | null };
    vehicle: { make: string | null; model: string | null; registration: string | null };
}

/**
 * The Borrower and the Vehicle as the Schedule's Parties table prints them.
 *
 * `profiles.admin_id` IS SELECTED SO THE RIGHT PROFILE CAN BE PICKED — the embed is unique only
 * where it is NULL, and taking `[0]` printed an admin's own details on a driver's contract once
 * already. `driverProfileOf` does the picking; this select's job is to hand it the column.
 *
 * THE PLATE COMES THROUGH `vehicles.registration_id`, never `vr.id = v.id`: the relationship runs
 * that way round, and the other reading has produced mis-joins in this codebase before.
 */
async function fetchAdvanceParties(contractId: number): Promise<PartyFacts | null> {
    if (!contractId) return null;
    const { data, error } = await supabase
        .from('contracts')
        .select(
            'id, ' +
                'drivers(profiles(id, admin_id, first_name, last_name, phone, address)), ' +
                'vehicles(id, make, model, vehicle_registrations!vehicles_registration_id_fkey(number))',
        )
        .eq('id', contractId)
        .single();
    if (error) throw error;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const row = data as any;
    const profile = driverProfileOf(row?.drivers?.profiles ?? null);
    const vehicle = Array.isArray(row?.vehicles) ? row.vehicles[0] : row?.vehicles;
    const registration = Array.isArray(vehicle?.vehicle_registrations)
        ? vehicle?.vehicle_registrations[0]
        : vehicle?.vehicle_registrations;

    return {
        driver: {
            fullName:
                [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim() || null,
            phone: profile?.phone ?? null,
            address: profile?.address ?? null,
        },
        vehicle: {
            make: vehicle?.make ?? null,
            model: vehicle?.model ?? null,
            registration: registration?.number ?? null,
        },
    };
}

interface ForwardFacts {
    /** The LATEST `contract_instalments.amount`, by `starts_at`. */
    contractInstalment: number | null;
    /** Every insurance premium advance's rate history on this contract, flattened. */
    insuranceLoanInstalments: WeeklyInstalmentRow[];
}

/**
 * What the contract already bills every Monday, in the terms `weekly_rate`'s fallback composes it
 * from — minus the `loans` half, which the board's subjects already carry.
 *
 * THE INSURANCE CHAIN IS READ WHOLE, NOT AT THE SETUP JUNCTION [D-B17]. `weekly_rate` sums the
 * latest rate row of EVERY insurance premium advance on the contract, renewal years included, so a
 * forward total narrowed to the earliest junction would under-state what the driver owes on the 84
 * contracts carrying a chain.
 *
 * EXPORTED BECAUSE THE POLICY DOSSIER READS THE SAME KEY, AND THEREFORE HAS TO SEND THE SAME
 * REQUEST. `advanceForwardKey` is one cache entry both pages consume; a second query function under
 * it would make the entry's contents depend on which page mounted first. That dossier narrows the
 * insurance half in a `useMemo` over the cached result instead (`useInsuranceState.ts`), so the
 * payload this returns stays whole for the panel on this page.
 */
export async function fetchForwardFacts(contractId: number): Promise<ForwardFacts | null> {
    if (!contractId) return null;

    const [instalments, junctions] = await Promise.all([
        supabase
            .from('contract_instalments')
            .select('amount, starts_at')
            .eq('contract_id', contractId)
            .order('starts_at', { ascending: false })
            .limit(1),
        supabase
            .from('insurance_contracts')
            .select('insurance_loans(id, insurance_loan_instalments(amount, starts_at))')
            .eq('contract_id', contractId),
    ]);

    if (instalments.error) throw instalments.error;
    if (junctions.error) throw junctions.error;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rows = (junctions.data ?? []) as any[];
    const insuranceLoanInstalments: WeeklyInstalmentRow[] = rows.flatMap((junction) =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (junction.insurance_loans ?? []).flatMap((loan: any) =>
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (loan.insurance_loan_instalments ?? []).map((row: any) => ({
                loanId: loan.id as number,
                amount: row.amount ?? null,
                startsAt: row.starts_at ?? null,
            })),
        ),
    );

    return {
        contractInstalment: instalments.data?.[0]?.amount ?? null,
        insuranceLoanInstalments,
    };
}

interface TransactionFacts {
    id: number;
    amount: number | null;
    transactionAt: string | null;
}

/** The disbursement's own row — read for the DATE the subjects embed does not ask for. */
async function fetchAdvanceTransaction(
    transactionId: number | null,
): Promise<TransactionFacts | null> {
    if (!transactionId) return null;
    const { data, error } = await supabase
        .from('transactions')
        .select('id, amount, transaction_at')
        .eq('id', transactionId)
        .single();
    if (error) throw error;
    return {
        id: data.id,
        amount: data.amount ?? null,
        transactionAt: data.transaction_at ?? null,
    };
}

/**
 * The first `loans.id` the allocator gates at its instalment week rather than at disbursement.
 *
 * NULL IS "NOT READ", AND THE CALLER TREATS IT AS LEGACY. See the file head: the conservative
 * failure is the one that offers no write.
 */
async function fetchRecoveryWatermark(): Promise<number | null> {
    const { data, error } = await supabase
        .from('allocation_gate_watermarks')
        .select('below_id')
        .eq('rule', RECOVERY_GATE_RULE)
        .eq('subject_table', 'loans')
        .maybeSingle();
    if (error) throw error;
    return data?.below_id ?? null;
}

// ─── The clock ───────────────────────────────────────────────────────────────

/** True while this advance's Schedule is signed by everyone and its executed PDF is not filed. */
function sealIsRunning(slot: BoardSlot | null): boolean {
    const document = slot?.document ?? null;
    return !!document && document.status === 'COMPLETED' && !document.finalized_at;
}

function useTickingClock(active: boolean): number {
    const [tick, setTick] = useState(() => Date.now());
    useEffect(() => {
        if (!active) return;
        const timer = window.setInterval(() => setTick(Date.now()), SEAL_TICK_MS);
        return () => window.clearInterval(timer);
    }, [active]);
    return active ? tick : 0;
}

// ─── The selector ────────────────────────────────────────────────────────────

export interface AdvanceStateResult {
    state: AdvanceState;
    snapshot: AdvanceSnapshot;
    /** Every underlying read has answered. The landing redirect waits for this. */
    stateReady: boolean;
    /** Where a route-less entry lands. Meaningless until `stateReady`. */
    landingArea: AdvanceArea;
    /** The advance as it stands on the server — what `validateDraft` judges an edit against. */
    existing: AdvanceExisting | null;
    /** This advance's row on the signing board, or null. */
    slot: BoardSlot | null;
    /** The forward sum's other terms — every advance EXCEPT the one being typed into. */
    forward: {
        contractInstalment: number | null;
        insuranceLoanInstalments: WeeklyInstalmentRow[];
        loanInstalments: WeeklyInstalmentRow[];
    };
    /** The advance's saved figures, as a starting draft. Empty on `new`. */
    saved: {
        transactionId: number | null;
        amount: number | null;
        interestRate: number | null;
        purpose: string | null;
        weeklyAmount: number | null;
        firstStartsAt: string | null;
    };
}

interface Options {
    contractId: number;
    /** The route's subject: an existing advance, or the create flow. */
    loanId: number | 'new';
    /** The PAGE's signing model, at `junctionScope: 'all'`. Never re-declared here. */
    agreements: ContractAgreementsModel;
    /** Unsaved local edits, by row id. Shown as a modifier; never counted as progress. */
    drafts?: AdvanceSnapshot['drafts'];
}

export function useAdvanceState({
    contractId,
    loanId,
    agreements,
    drafts,
}: Options): AdvanceStateResult {
    const queryClient = useQueryClient();
    const isNew = loanId === 'new';

    const { data: parties, isLoading: partiesLoading } = useQuery({
        queryKey: advancePartiesKey(contractId),
        queryFn: () => fetchAdvanceParties(contractId),
        enabled: !!contractId,
    });

    const { data: forwardFacts, isLoading: forwardLoading } = useQuery({
        queryKey: advanceForwardKey(contractId),
        queryFn: () => fetchForwardFacts(contractId),
        enabled: !!contractId,
    });

    const {
        data: watermark,
        isLoading: watermarkLoading,
        isError: watermarkFailed,
    } = useQuery({
        queryKey: RECOVERY_WATERMARK_KEY,
        queryFn: fetchRecoveryWatermark,
        staleTime: 5 * 60 * 1000,
    });

    /** The board's own reading of this contract's advances — see the file head. */
    const subjectLoan = useMemo(
        () =>
            isNew
                ? null
                : ((agreements.subjects?.loans ?? []).find((row) => row.id === loanId) ?? null),
        [agreements.subjects, isNew, loanId],
    );

    const { data: transaction, isLoading: transactionLoading } = useQuery({
        queryKey: advanceTransactionKey(subjectLoan?.transactionId ?? null),
        queryFn: () => fetchAdvanceTransaction(subjectLoan?.transactionId ?? null),
        enabled: !!subjectLoan?.transactionId,
    });

    const slot = useMemo<BoardSlot | null>(
        () => (isNew ? null : (loanSlotsForLoan(agreements.slots, loanId)[0] ?? null)),
        [agreements.slots, isNew, loanId],
    );

    const clock = useTickingClock(sealIsRunning(slot));

    /**
     * `missing[]` for the Schedule, ONCE A ROW HAS OPENED IT — read, never fetched.
     *
     * `['agreement-render', documentId]` is a lazy per-row entry and IS the shown-hash record every
     * freezing RPC compares against, so pulling it up to page level would render a document nobody
     * opened and move the hash a countersignature attests to. `ContractSetup.tsx` reads it exactly
     * this way, and the subscription is to the CACHE rather than to a query so nothing is issued.
     */
    const documentId = slot?.document?.id ?? null;
    const [renderMissing, setRenderMissing] = useState<string[] | null>(null);

    useEffect(() => {
        if (!documentId) {
            setRenderMissing((previous) => (previous === null ? previous : null));
            return;
        }
        const read = () => {
            const next =
                queryClient.getQueryData<AgreementRenderPayload>(['agreement-render', documentId])
                    ?.missing ?? null;
            setRenderMissing((previous) => (previous === next ? previous : next));
        };
        read();
        return queryClient.getQueryCache().subscribe((event) => {
            if (event.query.queryKey?.[0] === 'agreement-render') read();
        });
    }, [queryClient, documentId]);

    const instalments = useMemo<AdvanceInstalmentFact[]>(
        () =>
            (subjectLoan?.instalments ?? []).map((row) => ({
                amount: row.amount ?? null,
                startsAt: row.starts_at ?? null,
            })),
        [subjectLoan],
    );

    /**
     * [D-B54] — below the watermark and never scheduled.
     *
     * THE RULE ITSELF IS `advanceLegacyReason`'s, and it lives in the pure module beside the copy
     * each of its answers picks. This holds the three reads it is answered from and nothing else:
     * the board's own `failed`, the watermark, and the instalment rows the board carried. A read
     * that errored or a row that is not there shuts the same door under its own name, because "below
     * the watermark" is a claim about this advance's money and a failed read establishes nothing.
     */
    const legacyNoSchedule = useMemo<AdvanceLegacyReason | false>(
        () =>
            advanceLegacyReason({
                isNew,
                loanId,
                instalmentCount: instalments.length,
                watermark,
                watermarkFailed,
                boardFailed: agreements.failed,
            }),
        [isNew, loanId, instalments.length, watermark, watermarkFailed, agreements.failed],
    );

    /**
     * THE HIRE PURCHASE FACTS, OFF THE BOARD'S OWN SLOTS AND NEVER RE-DERIVED.
     *
     * `driverSigned` and `witnessSigned` are contract facts hung on EVERY slot by
     * `buildBoardSlots`, from the same rows `agreement_sign` reads — so this rail and the row
     * underneath it cannot disagree about the D-B55 lock. The reference is the live hire purchase
     * DOCUMENT's own designation, and where there is none, [D-B52]'s paper designation for a contract
     * whose hire purchase agreement was signed on paper.
     */
    const hpSlot = useMemo(
        () => agreements.slots.find((candidate) => candidate.docType === 'HIRE_PURCHASE') ?? null,
        [agreements.slots],
    );

    const hpReference = useMemo(() => {
        const live = hpSlot?.document?.reference_no ?? null;
        if (live) return live;
        const paperSigned =
            !!hpSlot?.legacySignedAt &&
            (hpSlot.legacyOrigin ?? 'SCANNED').toUpperCase() === 'SCANNED';
        return paperSigned ? `P-${contractId}` : null;
    }, [hpSlot, contractId]);

    /**
     * ANY SLOT WILL DO FOR THE TWO CROSS-DOCUMENT FACTS, and that is not laziness [D-B18].
     *
     * `driverSigned` and `witnessSigned` are facts about the CONTRACT, derived once by
     * `buildBoardSlots` and hung identically on every slot it mints — so whichever row answers, it
     * answers the same. This advance's own row is preferred only so the read is stable while the
     * board is still filling in.
     */
    const anySlot = slot ?? hpSlot ?? agreements.slots[0] ?? null;

    const snapshot = useMemo<AdvanceSnapshot>(
        () => ({
            contractId,
            now: clock ? new Date(clock) : new Date(),
            isNew,
            loan: subjectLoan
                ? {
                    id: subjectLoan.id,
                    purpose: subjectLoan.description,
                    interestRate: subjectLoan.interestRate,
                    transactionId: subjectLoan.transactionId,
                }
                : null,
            instalments,
            transaction: transaction
                ? { id: transaction.id, amount: transaction.amount, transactionAt: transaction.transactionAt }
                : subjectLoan?.transactionId
                  ? { id: subjectLoan.transactionId, amount: subjectLoan.amount, transactionAt: null }
                  : null,
            driver: parties?.driver ?? { fullName: null, phone: null, address: null },
            vehicle: parties?.vehicle ?? { make: null, model: null, registration: null },
            hpReference,
            hpDriverSigned: anySlot?.driverSigned ?? false,
            witnessSigned: anySlot?.witnessSigned ?? false,
            slot: slot
                ? {
                    legacySignedAt: slot.legacySignedAt,
                    legacyUrl: slot.legacyUrl,
                    legacyOrigin: slot.legacyOrigin,
                    document: slot.document
                        ? {
                            id: slot.document.id,
                            status: slot.document.status,
                            finalizedAt: slot.document.finalized_at,
                            missing: renderMissing,
                            signatures: [...slot.document.signatures]
                                .sort((a, b) => (a.sign_order ?? 0) - (b.sign_order ?? 0))
                                .map((signature) => ({
                                    party: signature.party,
                                    status: signature.status,
                                    signedAt: signature.signed_at,
                                })),
                        }
                        : null,
                    blockedReason: slot.blockedReason,
                }
                : null,
            renderMissing,
            legacyNoSchedule,
            frozen: !!slot?.document?.frozen_at,
            drafts,
        }),
        [
            contractId,
            clock,
            isNew,
            subjectLoan,
            instalments,
            transaction,
            parties,
            hpReference,
            anySlot,
            slot,
            renderMissing,
            legacyNoSchedule,
            drafts,
        ],
    );

    const state = useMemo(() => computeAdvanceState(snapshot), [snapshot]);

    /**
     * The OTHER advances' rate rows — this contract's `loans` minus the one being typed into.
     *
     * THE EXCLUSION IS THE WHOLE POINT (`forwardWeeklyTotal`'s contract): the typed figure IS this
     * advance's contribution, and leaving its saved rate in the list would count the old rate beside
     * the new one on every edit.
     */
    const otherLoanInstalments = useMemo<WeeklyInstalmentRow[]>(
        () =>
            (agreements.subjects?.loans ?? [])
                .filter((row) => isNew || row.id !== loanId)
                .flatMap((row) =>
                    (row.instalments ?? []).map((instalment) => ({
                        loanId: row.id,
                        amount: instalment.amount ?? null,
                        startsAt: instalment.starts_at ?? null,
                    })),
                ),
        [agreements.subjects, isNew, loanId],
    );

    const stateReady =
        !agreements.loading &&
        !partiesLoading &&
        !forwardLoading &&
        !watermarkLoading &&
        !transactionLoading;

    /**
     * THE SAVED FIGURES AND THE FORWARD TERMS ARE HELD, not rebuilt on every render.
     *
     * The page's form is `saved` with the admin's edits patched over it, and every derivation
     * downstream of that — the draft, the refusal list, the proof-read — is memoised on it. A fresh
     * object here would make all of those recompute on every keystroke anywhere on the page, and
     * would make their `useMemo`s a lie about what they depend on.
     */
    const existing = useMemo<AdvanceExisting | null>(
        () =>
            isNew
                ? null
                : {
                    instalments,
                    // THE PURPOSE ON FILE, SO A SAVE CANNOT EMPTY IT. `validateDraft` refuses a
                    // cleared purpose only where the server holds one — the Schedule cannot render
                    // without `{{loan.purpose}}`, and the one production advance that never had a
                    // description stays correctable in every other respect.
                    purpose: subjectLoan?.description ?? null,
                    // THE WRITER DOES NOT CARE WHY THE DOOR IS SHUT, only that it is.
                    legacyNoSchedule: legacyNoSchedule !== false,
                },
        [isNew, instalments, subjectLoan, legacyNoSchedule],
    );

    const forward = useMemo(
        () => ({
            contractInstalment: forwardFacts?.contractInstalment ?? null,
            insuranceLoanInstalments: forwardFacts?.insuranceLoanInstalments ?? [],
            loanInstalments: otherLoanInstalments,
        }),
        [forwardFacts, otherLoanInstalments],
    );

    /**
     * THE PAIR IS THE RAIL'S OWN, AND IT IS SPELLED ONCE. `latestInstalment` (MAX(`starts_at`), the
     * server's `>=` tie-break) and `firstStartsAt` (MIN) are `advanceState.ts`'s exports, already
     * driving `weeklyRow` and `firstRow` — a second spelling here would let the form and the rail
     * derive one figure two ways, which is how the previous screen came to draw `Insurance 3/4` over
     * four empty rows.
     */
    const saved = useMemo(
        () => ({
            transactionId: subjectLoan?.transactionId ?? null,
            amount: transaction?.amount ?? subjectLoan?.amount ?? null,
            interestRate: subjectLoan?.interestRate ?? null,
            purpose: subjectLoan?.description ?? null,
            weeklyAmount: latestInstalment(instalments)?.amount ?? null,
            firstStartsAt: firstStartsAt(instalments),
        }),
        [subjectLoan, transaction, instalments],
    );

    return {
        state,
        snapshot,
        stateReady,
        landingArea: resolveAdvanceLandingArea(state),
        existing,
        slot,
        forward,
        saved,
    };
}
